import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, Sun, Moon } from "lucide-react";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useTheme } from "next-themes";
import { trackEvent, ConversionEvents } from "@/lib/analytics";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import '@/pages/landing-animations.css';

// Top-of-funnel CTA — the global "Start for free" / "Sign up" button.
// Without this event we can't tell which surface (header, blog, pricing)
// brings visitors to /auth.
function trackHeaderCta(label: string, target: string) {
  trackEvent(ConversionEvents.CTA_CLICKED, {
    source: 'header',
    label: label.slice(0, 64),
    href: target.slice(0, 200),
    path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

export const MarketingHeader = ({ dashboardMode, onOpenSettings }: { dashboardMode?: boolean; onOpenSettings?: () => void } = {}) => {
  const { user, signOut } = useAuth();
  const { t } = useTranslation(['translation', 'common']);
  const { theme, resolvedTheme, setTheme } = useTheme();
  const lp = useLocalizedPath();

  const toggleTheme = () => {
    const isDark = resolvedTheme === 'dark';
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('cctvplanner_theme_chosen', next);
  };
  const [mounted, setMounted] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [disciplinesOpen, setDisciplinesOpen] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const productLinks = [
    { to: lp("/features"), label: t('features') },
    { to: lp("/how-it-works"), label: t('how_it_works') },
    { to: lp("/use-cases"), label: t('use_cases') },
  ];

  // Pillar pages from the 2026-05-16 repositioning sprint. Kept in a
  // separate dropdown so the existing "Product" menu stays untouched.
  const disciplinesLinks = [
    { to: lp("/access-control-design"), label: t('access_control_nav', { defaultValue: 'Access control' }) },
    { to: lp("/intrusion-detection-design"), label: t('intrusion_detection_nav', { defaultValue: 'Intrusion detection' }) },
    { to: lp("/network-planning-for-security"), label: t('network_planning_nav', { defaultValue: 'Network planning' }) },
    { to: lp("/security-compliance"), label: t('security_compliance_nav', { defaultValue: 'Compliance' }) },
  ];

  const navLinks = [
    { to: lp("/calculators"), label: t('calculators_nav'), badge: 'Free' },
    { to: lp("/pricing"), label: t('pricing') },
    { to: lp("/blog"), label: t('blog') },
    { to: lp("/docs"), label: t('docs_nav') },
  ];

  return (
    <>
      <header className="pelco-header">
        <div className="pelco-container h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo — brand refresh V4 horizontal lockup (mark + wordmark
                with .io accent). Light/dark variants swapped via CSS visibility. */}
            <Link to={lp("/")} className="flex-shrink-0 flex items-center" aria-label="CCTVplanner.io home">
              <img src="/logos/logo-horizontal-dark.svg" alt="CCTVplanner.io" className="h-9 w-auto block dark:hidden" />
              <img src="/logos/logo-horizontal-light.svg" alt="CCTVplanner.io" className="h-9 w-auto hidden dark:block" />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-6">
              {/* Product dropdown */}
              <div className="relative"
                onMouseEnter={() => setProductOpen(true)}
                onMouseLeave={() => setProductOpen(false)}
              >
                <button
                  className="pelco-nav-link flex items-center gap-1"
                  onClick={() => setProductOpen(!productOpen)}
                >
                  {t('footer_product', 'Product')}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${productOpen ? 'rotate-180' : ''}`} />
                </button>
                {productOpen && (
                  <div className="absolute top-full start-0 mt-1 w-52 py-2 rounded shadow-lg border" style={{ borderColor: 'var(--color-gray-border)', backgroundColor: 'var(--color-surface)' }}>
                    {productLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setProductOpen(false)}
                        className="block px-4 py-2 text-sm transition-colors"
                        style={{ color: 'var(--color-gray-dark)', fontFamily: 'Inter, sans-serif' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-hover-overlay)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Security disciplines dropdown — pillar pages for the broader
                  "security design platform" positioning (2026-05-16 sprint). */}
              <div className="relative"
                onMouseEnter={() => setDisciplinesOpen(true)}
                onMouseLeave={() => setDisciplinesOpen(false)}
              >
                <button
                  className="pelco-nav-link flex items-center gap-1"
                  onClick={() => setDisciplinesOpen(!disciplinesOpen)}
                >
                  {t('disciplines_nav', { defaultValue: 'Disciplines' })}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${disciplinesOpen ? 'rotate-180' : ''}`} />
                </button>
                {disciplinesOpen && (
                  <div className="absolute top-full start-0 mt-1 w-56 py-2 rounded shadow-lg border" style={{ borderColor: 'var(--color-gray-border)', backgroundColor: 'var(--color-surface)' }}>
                    {disciplinesLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setDisciplinesOpen(false)}
                        className="block px-4 py-2 text-sm transition-colors"
                        style={{ color: 'var(--color-gray-dark)', fontFamily: 'Inter, sans-serif' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-hover-overlay)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="pelco-nav-link flex items-center gap-1.5"
                >
                  {link.label}
                  {link.badge && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(56, 112, 230, 0.15)', color: 'var(--color-brand-blue)' }}>
                      {link.badge}
                    </span>
                  )}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <div className="hidden lg:flex items-center gap-2">
                <LanguageSelector />
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: 'var(--color-gray-muted)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-hover-overlay)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {mounted ? (resolvedTheme === 'dark' ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />) : <span className="w-4.5 h-4.5 inline-block" />}
                </button>
              </div>

              {mounted && user ? (
                <div className="hidden lg:flex items-center gap-3">
                  {dashboardMode ? (
                    <>
                      <button onClick={() => signOut()} className="pelco-nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                        {t('common:logout', { defaultValue: 'Log out' })}
                      </button>
                      {onOpenSettings && (
                        <button onClick={onOpenSettings} className="pelco-btn pelco-btn--primary" style={{ padding: '0.5rem 1.5rem', fontSize: '0.8125rem' }}>
                          {t('dashboard:settings_btn', { defaultValue: 'Settings' })}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <Link to="/dashboard" className="pelco-nav-link">
                        {t('common:dashboard')}
                      </Link>
                      <Link to="/cctv-design" className="pelco-btn pelco-btn--primary" style={{ padding: '0.5rem 1.5rem', fontSize: '0.8125rem' }}>
                        {t('plan_cta_get_started', { defaultValue: 'Start designing' })}
                      </Link>
                    </>
                  )}
                </div>
              ) : (
                <div className="hidden lg:flex items-center gap-3">
                  <Link to="/auth" className="pelco-nav-link">
                    {t('common:login')}
                  </Link>
                  <Link to="/auth?mode=signup&redirect=/cctv-design" className="pelco-btn pelco-btn--primary" style={{ padding: '0.5rem 1.5rem', fontSize: '0.8125rem' }}
                    onClick={() => trackHeaderCta(t('plan_cta_get_started', { defaultValue: 'Start for free' }), '/auth?mode=signup')}>
                    {t('plan_cta_get_started', { defaultValue: 'Start for free' })}
                  </Link>
                </div>
              )}

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 active:scale-95 active:opacity-70"
                style={{ color: 'var(--color-gray-dark)' }}
                aria-label={t('common:open_menu')}
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-0 end-0 bottom-0 w-[300px] max-w-[85vw] flex flex-col animate-in slide-in-from-right rtl:slide-in-from-left" style={{ backgroundColor: 'var(--color-page-bg)' }}>
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 border-b" style={{ height: 'var(--header-height)', borderColor: 'var(--color-gray-border)', paddingTop: 'env(safe-area-inset-top)' }}>
              <div className="flex items-center gap-2">
                {/* Mark-only (no rounded white panel) reads cleanly on the
                    drawer's page-bg; light/dark variants swap by theme. */}
                <img src="/logos/logo-mark.svg" alt="" className="w-7 h-7 block dark:hidden" />
                <img src="/logos/logo-mark-on-dark.svg" alt="" className="w-7 h-7 hidden dark:block" />
                <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.01em', color: 'var(--color-gray-dark)' }}>
                  CCTVplanner
                </span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-2 -me-2" style={{ color: 'var(--color-gray-muted)' }} aria-label={t('common:close_menu')}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer links */}
            <nav className="flex-1 overflow-y-auto px-5 py-6 space-y-1" style={{ fontFamily: 'Inter, sans-serif' }}>
              {productLinks.map((link) => (
                <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                  className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                  {link.label}
                </Link>
              ))}
              <div className="my-3" style={{ borderTop: '1px solid var(--color-gray-border)' }} />
              {disciplinesLinks.map((link) => (
                <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                  className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                  {link.label}
                </Link>
              ))}
              <div className="my-3" style={{ borderTop: '1px solid var(--color-gray-border)' }} />
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                  className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                  {link.label}
                </Link>
              ))}

              {mounted && user && (
                <>
                  <div className="my-3" style={{ borderTop: '1px solid var(--color-gray-border)' }} />
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)}
                    className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-dark)' }}>
                    {t('common:dashboard')}
                  </Link>
                  {dashboardMode && onOpenSettings && (
                    <button
                      onClick={() => { onOpenSettings(); setMobileOpen(false); }}
                      className="block py-3 text-base font-medium w-full text-start"
                      style={{ color: 'var(--color-gray-dark)' }}
                    >
                      {t('dashboard:settings_btn', { defaultValue: 'Settings' })}
                    </button>
                  )}
                  <button onClick={() => { signOut(); setMobileOpen(false); }}
                    className="block py-3 text-base font-medium text-red-500 w-full text-start">
                    {t('common:logout', { defaultValue: 'Log out' })}
                  </button>
                </>
              )}

              {mounted && !user && (
                <>
                  <div className="my-3" style={{ borderTop: '1px solid var(--color-gray-border)' }} />
                  <Link to="/auth" onClick={() => setMobileOpen(false)}
                    className="block py-3 text-base font-medium" style={{ color: 'var(--color-gray-muted)' }}>
                    {t('common:login')}
                  </Link>
                  <Link to="/auth?mode=signup&redirect=/cctv-design"
                    onClick={() => {
                      trackHeaderCta(t('plan_cta_get_started', { defaultValue: 'Start for free' }), '/auth?mode=signup');
                      setMobileOpen(false);
                    }}
                    className="block mt-2 py-3 text-center text-base font-bold bg-[var(--color-brand-blue)] text-white">
                    {t('plan_cta_get_started', { defaultValue: 'Start for free' })}
                  </Link>
                </>
              )}

              <div className="pt-4 flex items-center gap-3">
                <LanguageSelector />
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: 'var(--color-gray-muted)', border: '1px solid var(--color-gray-border)' }}
                  aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {mounted ? (resolvedTheme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />) : <span className="w-5 h-5 inline-block" />}
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};
